import { useMemo } from 'react';
import { formatNumber } from 'utils/number';
import { Button } from '@carbon/react';
import { ArrowLeft } from '@carbon/react/icons';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  AvailableAfe,
  PdpDeclineForecast,
  TypeCurveForecast,
} from 'types/api-responses';
import type { TractNRI } from '../types';
import {
  toBulkEditPatch,
  type BulkEditAfeOptions,
  type BulkEditFormState,
} from './types';
import {
  buildReviewRows,
  FIELD_LABELS,
  formatFieldValue,
  REVIEW_FIELD_ORDER,
  makeAfeLookup,
  makeVolumeSourceLookup,
  type ReviewRow,
} from './utils';

import style from './review-changes-view.module.scss';

/** The picker's loaded forecast lists — the id → name source for review display. */
type VolumeForecasts = {
  typeCurves: TypeCurveForecast[];
  pdpDeclines: PdpDeclineForecast[];
} | null;

interface ReviewChangesViewProps {
  selectedRows: TractNRI[];
  form: BulkEditFormState;
  afeOptions: BulkEditAfeOptions;
  /** Called when the user clicks the back link to return to the edit view. */
  onBack: () => void;
  /**
   * Optional resolver for the "original" value of a field on a slot. Use
   * this when the page maintains its own override state in addition to the
   * raw `TractNRI` row. Defaults to reading `row[key]`.
   */
  resolveOriginal?: (
    row: TractNRI,
    key: keyof BulkEditFormState
  ) => string | undefined;
  /**
   * The volume picker's loaded forecasts, so a reviewed `volumeSourceId` renders as
   * the forecast's NAME rather than its raw (often numeric) id. Optional: absent, the
   * id is shown — the pre-picker behavior.
   */
  volumeForecasts?: VolumeForecasts;
}

export const ReviewChangesView = ({
  selectedRows,
  form,
  afeOptions,
  onBack,
  resolveOriginal,
  volumeForecasts,
}: ReviewChangesViewProps) => {
  const patch = useMemo(() => toBulkEditPatch(form), [form]);
  const afeLookup = useMemo(
    () => makeAfeLookup(afeOptions.items),
    [afeOptions.items]
  );
  const volumeSourceLookup = useMemo(
    () => makeVolumeSourceLookup(volumeForecasts ?? null),
    [volumeForecasts]
  );

  const fieldKeys = useMemo(
    () =>
      REVIEW_FIELD_ORDER.filter(
        (key) => patch[key] !== undefined && patch[key] !== ''
      ),
    [patch]
  );

  const rows = useMemo(
    () =>
      buildReviewRows(
        selectedRows,
        patch,
        afeLookup,
        resolveOriginal,
        volumeSourceLookup
      ),
    [selectedRows, patch, afeLookup, resolveOriginal, volumeSourceLookup]
  );

  const columns: DataTableColumnDef<ReviewRow>[] = useMemo(() => {
    // Size each column to its widest rendered content (header text or any
    // cell) so the DataTable's text-overflow:ellipsis on .tableCellContent
    // doesn't clip the "original → new" comparison.
    const CHAR_PX = 7.5; // approx body-short-01 char width
    const CELL_PADDING = 32; // .tableCell horizontal padding (both sides)
    const HEADER_EXTRA = 8; // breathing room for the header label
    const ARROW_PX = 28; // .arrow span: glyph + margin on both sides
    const MIN_WIDTH = 80;

    const widthFor = (header: string, contentPx: number[]): number => {
      const headerPx = header.length * CHAR_PX + CELL_PADDING + HEADER_EXTRA;
      const contentMaxPx = contentPx.reduce(
        (m, px) => Math.max(m, px + CELL_PADDING),
        0
      );
      return Math.ceil(Math.max(headerPx, contentMaxPx, MIN_WIDTH));
    };

    const defs: DataTableColumnDef<ReviewRow>[] = [
      {
        id: 'slotId',
        accessorKey: 'slotId',
        header: 'Slot ID',
        size: widthFor(
          'Slot ID',
          rows.map((r) => r.slotId.length * CHAR_PX)
        ),
      },
    ];
    fieldKeys.forEach((key) => {
      defs.push({
        id: key,
        header: FIELD_LABELS[key],
        accessorFn: (row) => row.fields[key]?.newDisplay ?? '',
        size: widthFor(
          FIELD_LABELS[key],
          rows.map((r) => {
            const c = r.fields[key];
            if (!c) return CHAR_PX; // "—"
            return (
              c.originalDisplay.length * CHAR_PX +
              ARROW_PX +
              c.newDisplay.length * CHAR_PX
            );
          })
        ),
        cell: ({ row }) => {
          const cell = row.original.fields[key];
          if (!cell) return <>—</>;
          return (
            <>
              <span className={style.original}>{cell.originalDisplay}</span>
              <span className={style.arrow} aria-hidden="true">
                →
              </span>
              <span className={cell.changed ? style.newChanged : style.newSame}>
                {cell.newDisplay}
              </span>
            </>
          );
        },
      });
    });
    return defs;
  }, [fieldKeys, rows]);

  return (
    <div className={style.body}>
      <Button
        kind="ghost"
        size="sm"
        renderIcon={ArrowLeft}
        onClick={onBack}
        className={style.backLink}
      >
        Back to edit
      </Button>
      <h3 className={style.sectionTitle}>Review Changes</h3>
      <p className={style.description}>
        Review the comparison below before saving. Cells show each
        attribute&apos;s current value and new value. Blank cells are attributes
        you chose not to change.
      </p>

      {fieldKeys.length === 0 ? (
        <p className={style.empty}>
          No changes to review. Go back and enter at least one value.
        </p>
      ) : (
        <DataTable<ReviewRow>
          data={rows}
          columns={columns}
          getRowId={(row) => row.slotId}
          config={{
            columnResizing: false,
            columnReordering: false,
            columnPinning: false,
            sorting: false,
            columnVisibility: false,
            globalSearch: false,
            columnFilters: false,
            hideFooter: true,
            fitContent: true,
          }}
        />
      )}
    </div>
  );
};

interface ReviewAllSummaryProps {
  patch: Partial<BulkEditFormState>;
  total: number;
  excludedCount: number;
  /**
   * The select-all AFE selection, if any — the authoritative label source (the picker's
   * search page may not contain it, so we can't look it up by id). Rendered as its own row.
   */
  selectedAfe?: AvailableAfe | null;
  onBack: () => void;
  /** See ReviewChangesViewProps#volumeForecasts. */
  volumeForecasts?: VolumeForecasts;
}

/**
 * Count-based review for select-all mode — a summary of the fields being applied to all
 * `total` slots (`excludedCount` excluded). No per-row diff, since the rows aren't
 * materialized in select-all mode. Reuses the shared field labels + value formatting, and
 * shows the chosen AFE (from `selectedAfe`) as its own row.
 *
 * @param {ReviewAllSummaryProps} props - The patch, counts, chosen AFE, and the back handler.
 * @return {JSX.Element} The select-all review summary.
 */
export const ReviewAllSummary = ({
  patch,
  total,
  excludedCount,
  selectedAfe,
  onBack,
  volumeForecasts,
}: ReviewAllSummaryProps) => {
  const afeLookup = useMemo(() => makeAfeLookup([]), []);
  const volumeSourceLookup = useMemo(
    () => makeVolumeSourceLookup(volumeForecasts ?? null),
    [volumeForecasts]
  );
  const fieldKeys = useMemo(
    () =>
      REVIEW_FIELD_ORDER.filter(
        (key) => patch[key] !== undefined && patch[key] !== ''
      ),
    [patch]
  );
  const afeLabel = selectedAfe
    ? `${selectedAfe.id} · ${selectedAfe.well?.name ?? '—'}`
    : undefined;

  return (
    <div className={style.body}>
      <Button
        kind="ghost"
        size="sm"
        renderIcon={ArrowLeft}
        onClick={onBack}
        className={style.backLink}
      >
        Back to edit
      </Button>
      <h3 className={style.sectionTitle}>Review Changes</h3>
      <p className={style.description}>
        Applying the values below to all {formatNumber(total, 0)} slot
        {total === 1 ? '' : 's'}
        {excludedCount > 0
          ? ` (${formatNumber(excludedCount, 0)} excluded)`
          : ''}
        . Blank fields are left as-is.
      </p>
      {fieldKeys.length === 0 && !afeLabel ? (
        <p className={style.empty}>
          No changes to review. Go back and enter at least one value.
        </p>
      ) : (
        <ul className={style.summaryList}>
          {afeLabel && (
            <li className={style.summaryItem}>
              <span className={style.summaryLabel}>AFE</span>
              <span className={style.summaryValue}>{afeLabel}</span>
            </li>
          )}
          {fieldKeys.map((key) => (
            <li key={key} className={style.summaryItem}>
              <span className={style.summaryLabel}>{FIELD_LABELS[key]}</span>
              <span className={style.summaryValue}>
                {formatFieldValue(
                  key,
                  patch[key],
                  afeLookup,
                  volumeSourceLookup
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
